<template>
  <ViewerLayoutRoot class="video-owner-layout" data-testid="video-owner-layout">
    <VideoOwnerTopBar />

    <!-- Summary mode (non-theater): the tabbed Smart Actions/Chapters
         content is the focus, at every viewport. Desktop pairs it with a
         fixed-width sidebar (video, metadata, Smart Actions list); mobile
         shows the content alone — no video — since there's no room for
         both and the content is the point of Summary mode. This is the
         only section rendered; there's no second tabs row below it (see
         the v-if guard on the Content Tabs block further down). -->
    <div v-if="isSummaryLayout">
      <div v-if="isSmallMobile" class="mt-3 px-4">
        <ItemName />
        <ViewerModeToggle class="video-owner-layout__mobile-mode-toggle" />
      </div>
      <div class="video-owner-layout__summary-body px-4 sm:px-0">
        <SummaryModeContent class="video-owner-layout__summary-content" />
        <SummaryModeSidebar v-if="isDesktop" class="video-owner-layout__summary-sidebar" />
      </div>
    </div>

    <template v-else>
      <!-- Split view: Logs mode or desktop Video mode -->
      <template v-if="splitConfig">
        <SplitViewContainer
          :key="splitConfig.storageKey"
          class="video-owner-layout__split"
          :class="{ 'video-owner-layout__split--logs': isLogsMode }"
          :full-width="isTheaterModeEnabled"
          v-bind="splitConfig"
        >
          <template #left>
            <div class="video-owner-layout__video-col">
              <div class="video-owner-layout__video">
                <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
              </div>
              <MetadataSection :show-created-at="true" :show-duration="true" />
              <RequestDetailsCard v-if="isLogsMode && isDesktop" class="video-owner-layout__request-details" />
            </div>
          </template>
          <template #right>
            <div class="video-owner-layout__side-panel">
              <component :is="rightPanel" />
            </div>
          </template>
        </SplitViewContainer>
      </template>

      <!-- Stacked: mobile -->
      <div v-else class="video-owner-layout__media-row" :class="{ 'video-owner-layout__media-row--single': !rightPanel || isTheaterModeEnabled }">
        <div class="video-owner-layout__video-col">
          <div class="video-owner-layout__video">
            <ViewerContent :class="{ 'viewer-video-theater-frame': isTheaterModeEnabled }" />
          </div>
          <div v-if="isSmallMobile" class="mt-3 px-4">
            <ItemName />
            <ViewerModeToggle class="video-owner-layout__mobile-mode-toggle" />
          </div>
          <div class="px-4 sm:px-0">
            <MetadataSection :show-created-at="true" :show-duration="true" />
          </div>
        </div>
        <div v-if="rightPanel && !isTheaterModeEnabled" class="video-owner-layout__side-panel px-4 sm:px-0">
          <component :is="rightPanel" />
        </div>
      </div>
    </template>

    <!-- Content Tabs: not rendered in the Summary layout — its own Smart
         Actions/Chapters tabs are the only tab row. -->
    <div v-if="!isSummaryLayout" class="video-owner-layout__tabs px-4 sm:px-0">
      <VideoOwnerTabs :theater-panel="isTheaterModeEnabled ? rightPanel : null" :theater-panel-label="theaterPanelLabel" @addedComment="emit('addedComment')" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import ViewerModeToggle from '@/modules/viewer/components/ViewerModeToggle.vue';
import SmartActionsSidePanel from '@/modules/viewer/components/SmartActionsSidePanel.vue';
import SmartActionsUpsell from '@/modules/viewer/components/SmartActionsUpsell.vue';
import MeetingOwnerSidePanel from '@/modules/viewer/components/meeting/MeetingOwnerSidePanel.vue';
import LogsSidePanel from '@/modules/viewer/components/logs/LogsSidePanel.vue';
import LogsMobileNotice from '@/modules/viewer/components/logs/LogsMobileNotice.vue';
import SplitViewContainer from '@/modules/viewer/components/SplitViewContainer.vue';
import RequestDetailsCard from '@/modules/viewer/components/RequestDetailsCard.vue';
import VideoOwnerTopBar from './VideoOwnerTopBar.vue';
import VideoOwnerTabs from './VideoOwnerTabs.vue';
import SummaryModeContent from './SummaryModeContent.vue';
import SummaryModeSidebar from './SummaryModeSidebar.vue';
import MetadataSection from '../../shared/MetadataSection.vue';

import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useViewerModeStore } from '@/modules/viewer/store/useViewerModeStore';
import { useViewerTheaterMode } from '@/modules/viewer/composables/useViewerTheaterMode';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useViewerModeUrlSync } from '@/modules/viewer/composables/useViewerModeUrlSync';
import { useSmartActionsNoVoice } from '@/modules/viewer/composables/useSmartActionsNoVoice';
import { useMobileDetection } from '@/composables/useMobileDetection';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';
import { VIDEO_RIGHT_PANE_SPLIT_CONFIG } from '../../shared/videoSplitConfig';
const emit = defineEmits<{
  addedComment: [];
}>();

const { hasAiAddon } = useVideoAi();
const { isMeeting, meetingInfo } = storeToRefs(useViewerItemStore());
const { isLogsMode, isSummaryMode } = storeToRefs(useViewerModeStore());
const { theaterModeStore, isTheaterModeEnabled } = useViewerTheaterMode();
const layoutStore = useLayoutStore();

const { isMobile } = useViewerAvailableModes();
const { isMobile: isSmallMobile } = useMobileDetection();
const isDesktop = computed(() => !isMobile.value);
const { hasTerminalNoVoice } = useSmartActionsNoVoice();

// Summary mode gets a dedicated tabbed-content layout at every viewport,
// but only outside theater mode — theater mode needs the video pinned to
// the video column so it can go full-width, with Summary content reachable
// via the Content Tabs' Summary tab instead (which only renders outside
// this state).
const isSummaryLayout = computed(() => isSummaryMode.value && !isTheaterModeEnabled.value);

watch(
  isTheaterModeEnabled,
  isEnabled => {
    layoutStore.setTheaterSidebarCollapsed(isEnabled);
  },
  { immediate: true }
);

useViewerModeUrlSync();

const splitConfig = computed(() => {
  if (isLogsMode.value && isDesktop.value) {
    return { defaultRatio: 0.4, minRatio: 0.25, maxRatio: 0.6, storageKey: 'viewer-logs-split-ratio', reversed: true };
  }
  if (isDesktop.value && rightPanel.value) {
    return VIDEO_RIGHT_PANE_SPLIT_CONFIG;
  }
  return null;
});

const rightPanel = computed<Component | null>(() => {
  if (isLogsMode.value) return isDesktop.value ? LogsSidePanel : LogsMobileNotice;
  if (isMeeting.value && !!meetingInfo.value) return MeetingOwnerSidePanel;
  if (hasTerminalNoVoice.value) return null;
  if (hasAiAddon.value) return SmartActionsSidePanel;
  return SmartActionsUpsell;
});

const theaterPanelLabel = computed(() => {
  if (isMeeting.value && !!meetingInfo.value) return 'Meeting Details';
  return hasAiAddon.value ? 'Smart Actions' : 'Unlock Smart Actions';
});

// Reserve scrollbar gutter on the document while the owner video layout is
// mounted. Without this, clicking a bottom tab (Summary → Transcript, etc.)
// that changes the tabs content height can cause the viewport scrollbar to
// appear/disappear, which shifts the layout by ~15px. Percentage-based
// widths in the split view cascade that change into the video's aspect-ratio
// height, producing a visible "dance" of the video and logs panels.
onMounted(() => {
  document.documentElement.classList.add('viewer-stable-scrollbar');
});

onUnmounted(() => {
  document.documentElement.classList.remove('viewer-stable-scrollbar');
  layoutStore.setTheaterSidebarCollapsed(false);
  theaterModeStore.disableTheaterMode();
});
</script>

<style scoped>
.video-owner-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-owner-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }

  .video-owner-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1440px) {
  .video-owner-layout__media-row {
    grid-template-columns: 1fr 360px;
  }

  .video-owner-layout__media-row--single {
    grid-template-columns: 1fr;
  }
}

.video-owner-layout__video-col {
  @apply flex flex-col min-w-0;
}

.video-owner-layout__request-details {
  @apply mt-5;
}

.video-owner-layout__mobile-mode-toggle {
  @apply mt-2 -ml-3;
}

/* Summary mode: content-only on mobile (no sidebar, no video). Desktop adds
   a fixed-width sidebar next to flexible content, both sized to their own
   natural height — no draggable split. */
.video-owner-layout__summary-body {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-owner-layout__summary-body {
    @apply flex-row items-start gap-6;
  }
}

.video-owner-layout__summary-content {
  @apply flex-1 min-w-0;
}

.video-owner-layout__summary-sidebar {
  width: 446px;
  flex-shrink: 0;
}

@media (min-width: 1800px) {
  .video-owner-layout__summary-content,
  .video-owner-layout__summary-sidebar {
    flex: 1 1 50%;
  }

  .video-owner-layout__summary-sidebar {
    width: auto;
    min-width: 446px;
  }
}

.video-owner-layout__video {
  @apply min-w-0;
}

.video-owner-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

@media (min-width: 1024px) {
  .video-owner-layout__video :deep(.viewer-video-theater-frame) {
    height: calc(100vh - 14rem);
    height: calc(100dvh - 14rem);
    max-height: calc(100vh - 14rem);
    max-height: calc(100dvh - 14rem);
    aspect-ratio: auto;
  }

  .video-owner-layout__video :deep(.viewer-video-theater-frame .video-container),
  .video-owner-layout__video :deep(.viewer-video-theater-frame .video-js) {
    height: 100%;
  }

  .video-owner-layout__video :deep(.viewer-video-theater-frame .vjs-tech) {
    object-fit: contain;
  }
}

/* Mobile: side panel below video, collapsible */
.video-owner-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

/* Desktop: side column */
@media (min-width: 1024px) {
  .video-owner-layout__side-panel {
    @apply mt-0;
  }

  .video-owner-layout__side-panel :deep(.smart-actions-panel),
  .video-owner-layout__side-panel :deep(.meeting-owner-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-owner-layout__tabs {
  @apply mt-2;
}

/* Split view (Logs mode) */
.video-owner-layout__split {
  min-height: 0;
}

.video-owner-layout__split :deep(.video-owner-layout__side-panel) {
  @apply mt-0 h-full;
}

.video-owner-layout__split :deep(.logs-panel) {
  @apply h-full;
}

.video-owner-layout__split :deep(.video-owner-layout__video-col) {
  @apply h-full;
}
</style>
